/**
 * Mock session — stand-in for real auth state until Phase 2.
 *
 * Tracks which of the mock test accounts (see mockTestAccounts in
 * mockData.ts) is "logged in" for this browser session, so different
 * accounts can see role-appropriate data (e.g. useNotifications filtering
 * by the current session's user id) without a real backend or JWT.
 *
 * Defaults to CURRENT_USER_ID (the content-creator persona used to build
 * all the existing mock notification examples) so the app behaves exactly
 * as before if nobody has gone through /login yet.
 *
 * Uses useSyncExternalStore so React components re-render when the
 * session changes (e.g. right after login) without needing a Context
 * provider wired through the whole tree.
 */
import { useSyncExternalStore } from 'react';

import { CURRENT_USER_ID } from '@/lib/mockData';

export interface MockSession {
  userId: string;
  email: string;
  displayName: string;
  roleLabel: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

let currentSession: MockSession | null = null;
const listeners = new Set<() => void>();

/**
 * Default session shown before anyone logs in — the content-creator
 * persona used to build all the existing mock notification examples.
 * Hoisted to a stable module-level constant (not built fresh inside
 * getSnapshot) because useSyncExternalStore requires getSnapshot to
 * return the same reference across calls when nothing has changed;
 * returning a new object literal every call causes React to throw
 * ("Maximum update depth exceeded") and blanks the whole page.
 */
const DEFAULT_SESSION: MockSession = {
  userId: CURRENT_USER_ID,
  email: 'contentcreator@jemmi.fun',
  displayName: 'Mina K.',
  roleLabel: 'Challenge creator',
  isAdmin: false,
  isSuperAdmin: false,
};

function emitChange() {
  for (const listener of listeners) listener();
}

export function setMockSession(session: MockSession) {
  currentSession = session;
  emitChange();
}

export function clearMockSession() {
  currentSession = null;
  emitChange();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): MockSession {
  return currentSession ?? DEFAULT_SESSION;
}

/** Read-only hook: the current mock session, reactive to login/logout. */
export function useMockSession(): MockSession {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Non-reactive read, for use outside React (e.g. the mock API layer). */
export function getMockSessionUserId(): string {
  return getSnapshot().userId;
}
